import { Typography } from "antd";
import ResizableDrawer from "@/components/UI/ResizableDrawer";
import FolderIcon from "@/assets/icons/folder_off.svg?react";
import AvatarWithInitials from "@/components/UI/AvatarWithInitials";
import StatusTransition from "@/components/UI/StatusTransition";
import { TableRow as DetailsRow } from "@/views/DataDiscrepancies/Components";
import { formatDateTimeWithAt } from "@/utils/dateUtils";
import { renderCell } from "@/utils/tableConfigs/GlobalFunctions";
import type { TableRow } from "./issuesTableConfig";
import LocalAssigneeSelect from "./LocalAssigneeSelect";
import { renderDateCell } from "./utils";

const { Title } = Typography;

type MockHistoryItem = {
  actor: string;
  action: string;
  at: string;
  transition?: {
    type:
      | "discrepancy_assignee"
      | "discrepancy_status"
      | "discrepancy_resolution_status";
    fromValue?: string | null;
    toValue?: string | null;
  };
};

const getStringValue = (value: TableRow[string]) => {
  return typeof value === "string" && value.trim().length > 0 ? value : null;
};

const createMockHistory = (record: TableRow): MockHistoryItem[] => {
  const identificationDate = getStringValue(
    record.discrepancy_identification_datetime,
  );
  const assignmentDate = getStringValue(
    record.discrepancy_assignments_last_modified_datetime,
  );
  const resolutionDate = getStringValue(record.discrepancy_resolution_datetime);

  const historyItems: MockHistoryItem[] = [];

  if (identificationDate) {
    historyItems.push({
      actor:
        getStringValue(record.discrepancy_last_modified_by) ||
        getStringValue(record.last_modified_by) ||
        "System",
      action: "identified this issue",
      at: identificationDate,
    });
  }

  if (assignmentDate) {
    historyItems.push({
      actor:
        getStringValue(record.discrepancy_assignment_last_modified_by) ||
        getStringValue(record.discrepancy_assignee) ||
        "System",
      action: "updated the assignee",
      at: assignmentDate,
      transition: {
        type: "discrepancy_assignee",
        fromValue: getStringValue(record.assignee),
        toValue: getStringValue(record.discrepancy_assignee),
      },
    });
  }

  if (
    resolutionDate &&
    getStringValue(record.discrepancy_resolution_status) &&
    getStringValue(record.discrepancy_resolution_status) !== "Unresolved"
  ) {
    historyItems.push({
      actor:
        getStringValue(record.discrepancy_last_modified_by) ||
        getStringValue(record.last_modified_by) ||
        "System",
      action: "updated the resolution status",
      at: resolutionDate,
      transition: {
        type: "discrepancy_resolution_status",
        fromValue: "Unresolved",
        toValue: getStringValue(record.discrepancy_resolution_status),
      },
    });
  }

  return historyItems.sort(
    (left, right) => new Date(right.at).getTime() - new Date(left.at).getTime(),
  );
};

type TrialIssuesDrawerProps = {
  selectedRecord: TableRow | null;
  setSelectedRecord: (record: TableRow | null) => void;
  assigneeOptions: string[];
  onAssigneeChange: (issueId: string, nextAssignee: string | null) => void;
};

export default function TrialIssuesDrawer({
  selectedRecord,
  setSelectedRecord,
  assigneeOptions,
  onAssigneeChange,
}: TrialIssuesDrawerProps) {
  const mockHistory = selectedRecord ? createMockHistory(selectedRecord) : [];

  const headersArray = [
    {
      key: "ISSUE ID",
      value: (
        <span className="text-white">{selectedRecord?.issue_id || "-"}</span>
      ),
    },
  ];

  return (
    <ResizableDrawer
      headerTitle="Trial Issue Information"
      headersArray={headersArray}
      sizeToggle={{
        collapsedWidth: 654,
        expandedWidth: 1250,
        initialWidth: 654,
      }}
      placement="right"
      open={selectedRecord !== null}
      className="data-management-drawer"
      onClose={() => setSelectedRecord(null)}
      closeIcon={null}
      closable={false}
    >
      {selectedRecord && (
        <div className="flex flex-col gap-6">
          <section>
            <Title level={5}>Information</Title>
            <div className="bg-white rounded-sm">
              <table className="w-full p-0 px-4  divide-solid divide-y divide-[#F0F0F0] ">
                <tbody className="p-0 divide-y divide-solid  divide-[#F0F0F0]">
                  <DetailsRow
                    label="Details"
                    value={renderCell(selectedRecord.discrepancy_details)}
                  />
                  <DetailsRow
                    label="Level"
                    value={renderCell(selectedRecord.discrepancy_level)}
                  />
                  <DetailsRow
                    label="Identification Datetime"
                    value={renderDateCell(
                      selectedRecord.discrepancy_identification_datetime,
                    )}
                  />
                  <DetailsRow
                    label="Type"
                    value={renderCell(selectedRecord.discrepancy_type)}
                  />
                </tbody>
              </table>
            </div>
          </section>
          <section>
            <Title level={5}>Impacted Subject / Site</Title>
            <div className="bg-white rounded-sm">
              <table className="w-full p-0 px-4  divide-solid divide-y divide-[#F0F0F0] ">
                <tbody className="p-0 divide-y divide-solid  divide-[#F0F0F0]">
                  <DetailsRow
                    label="Subject"
                    value={renderCell(selectedRecord.subject)}
                  />
                  <DetailsRow
                    label="Site"
                    value={renderCell(selectedRecord.site)}
                  />
                  <DetailsRow
                    label="Study Status"
                    value={
                      getStringValue(selectedRecord.study_status)
                        ? renderCell(
                            selectedRecord.study_status,
                            "study_status",
                          )
                        : "-"
                    }
                  />
                </tbody>
              </table>
            </div>
          </section>
          <section>
            <Title level={5}>Assignment Information</Title>
            <div className="bg-white rounded-sm">
              <table className="w-full p-0 px-4  divide-solid divide-y divide-[#F0F0F0] ">
                <tbody className="p-0 divide-y divide-solid  divide-[#F0F0F0]">
                  <DetailsRow
                    label="Assignee"
                    value={
                      typeof selectedRecord.discrepancy_id === "string" ? (
                        <LocalAssigneeSelect
                          value={selectedRecord.discrepancy_assignee}
                          options={assigneeOptions}
                          compact
                          onChange={(nextAssignee) =>
                            onAssigneeChange(
                              selectedRecord.discrepancy_id as string,
                              nextAssignee,
                            )
                          }
                        />
                      ) : (
                        "-"
                      )
                    }
                  />
                  <DetailsRow
                    label="Last Modified By"
                    value={renderCell(selectedRecord.last_modified_by)}
                  />
                  <DetailsRow
                    label="Assignment Last Modified By"
                    value={renderCell(
                      selectedRecord.discrepancy_assignment_last_modified_by,
                    )}
                  />
                  <DetailsRow
                    label="Assignment Last Modified Datetime"
                    value={renderDateCell(
                      selectedRecord.discrepancy_assignments_last_modified_datetime,
                    )}
                  />
                  <DetailsRow
                    label="Visit"
                    value={renderCell(selectedRecord.discrepancy_trial_visit)}
                  />
                </tbody>
              </table>
            </div>
          </section>
          <section>
            <Title level={5}>History</Title>
            <div>
              {mockHistory.length === 0 ? (
                <div className="flex h-28 flex-col items-center justify-center text-[#00000073]">
                  <FolderIcon />
                  No user history
                </div>
              ) : (
                <div className="px-2">
                  {mockHistory.map((historyItem, index) => (
                    <div
                      key={`${historyItem.action}-${historyItem.at}-${index}`}
                      className="my-7 flex flex-col"
                    >
                      <div className="flex items-center gap-1">
                        <AvatarWithInitials
                          name={historyItem.actor}
                          avatarSize={28}
                          labelFontSize={14}
                        />
                        {historyItem.action}
                        <div className="text-[#00000073]">
                          {formatDateTimeWithAt(historyItem.at)}
                        </div>
                      </div>
                      {historyItem.transition ? (
                        <div className="ml-10 mt-2 flex gap-2">
                          <StatusTransition
                            type={historyItem.transition.type}
                            fromValue={historyItem.transition.fromValue}
                            toValue={historyItem.transition.toValue}
                            avatarConfig={{
                              avatarSize: 28,
                              labelFontSize: 14,
                            }}
                          />
                        </div>
                      ) : null}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>
        </div>
      )}
    </ResizableDrawer>
  );
}
